import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { anglesService } from '../services/angles.service';
import { toast } from '@/shared/lib/toast';
import type { ProjectAngle } from '@/types/project-angle';

// Query Keys
export const projectAngleKeys = {
  all: ['project-angles'] as const,
  lists: () => [...projectAngleKeys.all, 'list'] as const,
  list: (projectId: string) =>
    [...projectAngleKeys.lists(), projectId] as const,
  details: () => [...projectAngleKeys.all, 'detail'] as const,
  detail: (angleId: string) =>
    [...projectAngleKeys.details(), angleId] as const,
};

/**
 * Hook to fetch all project angles
 */
export function useProjectAngles(projectId: string) {
  return useQuery({
    queryKey: projectAngleKeys.list(projectId),
    queryFn: () => anglesService.listProjectAngles(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for creating a new project angle
 */
export function useCreateProjectAngle(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['angles', 'create'],
    mutationFn: async ({
      fileName,
      md5,
      contentType,
      width,
      height,
      aspectRatio,
    }: {
      fileName: string;
      md5: string;
      contentType: string;
      width: number;
      height: number;
      aspectRatio: string;
    }) => {
      return anglesService.createProjectAngle(
        projectId,
        fileName,
        md5,
        contentType,
        width,
        height,
        aspectRatio
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
    },
  });
}

/**
 * Hook for confirming a project angle upload
 */
export function useConfirmProjectAngle(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['angles', 'confirm'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: async (angleId: string) => {
      return anglesService.confirmAngle(angleId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
    },
  });
}

/**
 * Hook for updating a project angle (name/description)
 */
export function useUpdateProjectAngle(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['angles', 'update'],
    meta: {
      lockId: (vars: unknown) => (vars as { angleId: string }).angleId,
    },
    mutationFn: async ({
      angleId,
      updates,
    }: {
      angleId: string;
      updates: { name?: string | null; description?: string | null };
    }) => {
      return anglesService.updateAngle(angleId, updates);
    },
    onMutate: async ({ angleId, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: projectAngleKeys.list(projectId),
      });

      // Snapshot the previous value
      const previousAngles = queryClient.getQueryData<ProjectAngle[]>(
        projectAngleKeys.list(projectId)
      );

      // Optimistically update to the new value
      if (previousAngles) {
        queryClient.setQueryData<ProjectAngle[]>(
          projectAngleKeys.list(projectId),
          previousAngles.map((angle) =>
            angle.id === angleId
              ? {
                  ...angle,
                  attributes: {
                    ...angle.attributes,
                    name:
                      updates.name !== undefined
                        ? updates.name
                        : angle.attributes.name,
                    description:
                      updates.description !== undefined
                        ? updates.description
                        : angle.attributes.description,
                  },
                }
              : angle
          )
        );
      }

      return { previousAngles };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousAngles) {
        queryClient.setQueryData(
          projectAngleKeys.list(projectId),
          context.previousAngles
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
    },
  });
}

/**
 * Hook for deleting a project angle with undo support.
 * Soft-deletes immediately, shows undo toast for 120 seconds.
 */
export function useDeleteProjectAngle(projectId: string) {
  const queryClient = useQueryClient();
  const t = useTranslations('angles');
  const tCommon = useTranslations('common');

  return useMutation({
    mutationKey: ['angles', 'delete'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: async (angleId: string) => {
      return anglesService.deleteAngle(angleId);
    },
    onMutate: async (angleId) => {
      await queryClient.cancelQueries({
        queryKey: projectAngleKeys.list(projectId),
      });

      const previousAngles = queryClient.getQueryData<ProjectAngle[]>(
        projectAngleKeys.list(projectId)
      );
      const deletedAngle = previousAngles?.find((a) => a.id === angleId);

      if (previousAngles) {
        queryClient.setQueryData<ProjectAngle[]>(
          projectAngleKeys.list(projectId),
          previousAngles.filter((angle) => angle.id !== angleId)
        );
      }

      return { previousAngles, deletedAngle };
    },
    onError: (_err, _angleId, context) => {
      if (context?.previousAngles) {
        queryClient.setQueryData(
          projectAngleKeys.list(projectId),
          context.previousAngles
        );
      }
    },
    onSuccess: (_data, angleId, context) => {
      if (!context?.deletedAngle) return;
      const angle = context.deletedAngle;

      toast.undoable(t('deleted'), {
        action: {
          label: tCommon('undo'),
          onClick: async () => {
            // Optimistic restore
            queryClient.setQueryData<ProjectAngle[]>(
              projectAngleKeys.list(projectId),
              (old) => {
                const current = old || [];
                if (current.some((a) => a.id === angleId)) return current;
                return [...current, angle];
              }
            );
            try {
              await anglesService.restoreAngle(angleId);
            } catch {
              toast.error(t('failedToRestore'));
              queryClient.setQueryData<ProjectAngle[]>(
                projectAngleKeys.list(projectId),
                (old) => (old || []).filter((a) => a.id !== angleId)
              );
            }
            queryClient.invalidateQueries({
              queryKey: projectAngleKeys.list(projectId),
            });
          },
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
    },
  });
}

/**
 * Hook for setting an angle as main
 */
export function useSetMainAngle(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['angles', 'set-main'],
    meta: { lockId: (vars: unknown) => vars as string },
    mutationFn: async (angleId: string) => {
      return anglesService.setMainAngle(projectId, angleId);
    },
    onMutate: async (angleId) => {
      await queryClient.cancelQueries({
        queryKey: projectAngleKeys.list(projectId),
      });

      const previousAngles = queryClient.getQueryData<ProjectAngle[]>(
        projectAngleKeys.list(projectId)
      );

      // Optimistically update main status
      if (previousAngles) {
        queryClient.setQueryData<ProjectAngle[]>(
          projectAngleKeys.list(projectId),
          previousAngles.map((angle) => ({
            ...angle,
            attributes: {
              ...angle.attributes,
              is_main: angle.id === angleId,
            },
          }))
        );
      }

      return { previousAngles };
    },
    onError: (_err, _angleId, context) => {
      if (context?.previousAngles) {
        queryClient.setQueryData(
          projectAngleKeys.list(projectId),
          context.previousAngles
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
    },
  });
}

/**
 * Hook for reordering project angles
 */
export function useReorderProjectAngles(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    // Bulk reorder — no single id to lock against; lock factory skips when
    // `mutationKey[2]` is absent. The post-success invalidate refetches the
    // canonical order, so a racing WS event resolves there.
    mutationKey: ['angles', 'reorder'],
    mutationFn: async (angleIds: string[]) => {
      return anglesService.reorderProjectAngles(projectId, angleIds);
    },
    onMutate: async (angleIds) => {
      await queryClient.cancelQueries({
        queryKey: projectAngleKeys.list(projectId),
      });

      const previousAngles = queryClient.getQueryData<ProjectAngle[]>(
        projectAngleKeys.list(projectId)
      );

      // Optimistically reorder angles
      if (previousAngles) {
        const angleMap = new Map(previousAngles.map((a) => [a.id, a]));
        const reorderedAngles = angleIds
          .map((id, index) => {
            const angle = angleMap.get(id);
            if (!angle) return null;
            return {
              ...angle,
              attributes: {
                ...angle.attributes,
                order: index,
                is_main: index === 0, // First angle becomes main
              },
            };
          })
          .filter((a): a is ProjectAngle => a !== null);

        queryClient.setQueryData<ProjectAngle[]>(
          projectAngleKeys.list(projectId),
          reorderedAngles
        );
      }

      return { previousAngles };
    },
    onError: (_err, _angleIds, context) => {
      if (context?.previousAngles) {
        queryClient.setQueryData(
          projectAngleKeys.list(projectId),
          context.previousAngles
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: projectAngleKeys.list(projectId),
      });
    },
  });
}
